/**
 * Typed readers for parsed-JSON values. A snapshot arrives as `unknown`, and
 * the sim state is not a shape it is safe to assert into existence — a save
 * from an older build, or a hand-edited file, would sail straight through a
 * cast and only fail thousands of ticks later as a `NaN`. Every field is read
 * and checked instead, so a bad snapshot fails at load with the path in the
 * message.
 */

export type PlainObject = Readonly<Record<string, unknown>>;

export function isPlainObject(value: unknown): value is PlainObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function requireObject(value: unknown, path: string): PlainObject {
  if (!isPlainObject(value)) throw new TypeError(`${path}: expected an object, got ${typeof value}`);
  return value;
}

export function readObject(source: PlainObject, key: string): PlainObject {
  return requireObject(source[key], key);
}

export function readNumber(source: PlainObject, key: string): number {
  const value = source[key];
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError(`${key}: expected a finite number, got ${String(value)}`);
  }
  return value;
}

export function readBoolean(source: PlainObject, key: string): boolean {
  const value = source[key];
  if (typeof value !== "boolean") throw new TypeError(`${key}: expected a boolean, got ${typeof value}`);
  return value;
}

/** Reads a string that must be one of a known set, returning it narrowed to that set. */
export function readMember<T extends string>(source: PlainObject, key: string, allowed: readonly T[]): T {
  const value = source[key];
  const match = allowed.find((candidate) => candidate === value);
  if (match === undefined) throw new TypeError(`${key}: expected one of ${allowed.join("|")}, got ${String(value)}`);
  return match;
}

/** Reads an array of strings drawn from a known set (unknown members are an error, not a silent drop). */
export function readMemberArray<T extends string>(
  source: PlainObject,
  key: string,
  allowed: readonly T[],
): T[] {
  const value = source[key];
  if (!Array.isArray(value)) throw new TypeError(`${key}: expected an array, got ${typeof value}`);
  return value.map((entry, index) => {
    const match = allowed.find((candidate) => candidate === entry);
    if (match === undefined) throw new TypeError(`${key}[${index}]: unknown value ${String(entry)}`);
    return match;
  });
}
